'use client';

import type { SyntheticEvent } from 'react';
import { CustomImage } from 'components/shared/custom-image/CustomImage';
import CustomLink from 'components/shared/CustomLink';
import { homepageCtaProps } from 'components/landing/v3/homepageCta';
import v3 from 'components/landing/v3/v3.module.css';
import type { ProductSpotlightConfig } from './types';
import styles from './StickyMediaRail.module.css';

type StickyMediaRailProps = {
  product: ProductSpotlightConfig;
  nameId: string;
};

export const StickyMediaRail = ({ product, nameId }: StickyMediaRailProps) => {
  const isDark = product.theme === 'dark';

  return (
    <div className={styles.rail}>
      <div
        className={`${styles.media} ${
          isDark ? styles.mediaDark : styles.mediaLight
        }`}
      >
        {product.video ? (
          <>
            <video
              className={styles.bannerVideo}
              autoPlay
              muted
              loop
              playsInline
              controls={false}
              disablePictureInPicture
              preload="auto"
              poster={product.videoPoster ?? product.image}
              aria-label={product.imageAlt}
              onLoadedData={(event: SyntheticEvent<HTMLVideoElement>) => {
                const video = event.currentTarget;
                video.defaultMuted = true;
                video.muted = true;
                video.setAttribute('webkit-playsinline', 'true');
                void video.play().catch(() => undefined);
              }}
            >
              <source src={product.video} type="video/mp4" />
            </video>
            <CustomImage
              className={`${styles.bannerImage} ${styles.reducedMotionImage}`}
              src={product.image}
              alt={product.imageAlt}
              fill
              sizes="(min-width: 992px) 50vw, 100vw"
            />
          </>
        ) : (
          <CustomImage
            className={styles.bannerImage}
            src={product.image}
            alt={product.imageAlt}
            fill
            sizes="(min-width: 992px) 50vw, 100vw"
            priority={product.id === 'ring-pro'}
          />
        )}
      </div>

      <div className={styles.metaRow}>
        <h3
          id={nameId}
          className={`${styles.name} ${
            isDark ? styles.nameDark : styles.nameLight
          }`}
          data-ff-name
        >
          {product.name}
        </h3>
        <div className={styles.ctaRow}>
          {product.primaryCta ? (
            <CustomLink
              href={product.primaryCta.href}
              className={`${v3.pill} ${v3.pillPrimary} ${styles.cta} ${styles.primaryCta}`}
              {...homepageCtaProps(
                product.primaryCta.buttonType,
                product.primaryCta.legacyButtonType,
              )}
            >
              {product.primaryCta.label}
            </CustomLink>
          ) : null}
          {product.unavailableNote ? (
            <p
              className={`${styles.note} ${
                isDark ? styles.noteDark : styles.noteLight
              }`}
            >
              {product.unavailableNote}
            </p>
          ) : null}
          <CustomLink
            href={product.secondaryCta.href}
            className={`${v3.pill} ${v3.pillSecondary} ${
              isDark ? v3.pillSecondaryOnDark : ''
            } ${styles.cta}`}
            {...homepageCtaProps(
              product.secondaryCta.buttonType,
              product.secondaryCta.legacyButtonType,
            )}
          >
            {product.secondaryCta.label}
          </CustomLink>
        </div>
      </div>
    </div>
  );
};
